import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { ExpansionPanelComponent } from '@connect-services/src/lib/components/expansion-panel/expansion-panel.component';
import { RessourcesModule } from '@ressources/client/ressources.module';

type Metadata = Record<string, string | null>;

export class MetadataInformation {
    public depositFolderMetadata?: Metadata | null;
    public depositMetadata?: Metadata | null;
    public standardMetadata?: Metadata | null;
}

type MetadataPanelKey = 'deposit' | 'standard';

class MetadataEntry {
    public label!: string;
    public value!: string | null;

    public static sort(entryA: MetadataEntry, entryB: MetadataEntry): number {
        return entryA.label.localeCompare(entryB.label);
    }
}

type MetadataPanel = {
    entries: MetadataEntry[];
    key: MetadataPanelKey;
    label: string;
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ExpansionPanelComponent, RessourcesModule],
    selector: 'app-deposit-metadata-information',
    templateUrl: './deposit-metadata-information.component.html',
})
export class DepositMetadataInformationComponent {
    public readonly metadataInformation = input.required<MetadataInformation>();
    public readonly metadataPanels = computed<MetadataPanel[]>(() => {
        const metadataInformation = this.metadataInformation();

        const depositAndDepositFolderMetadata = this.mergeMetadata(
            metadataInformation.depositFolderMetadata,
            metadataInformation.depositMetadata,
        );

        const standardMetadata = metadataInformation.standardMetadata ?? null;

        return [
            this.buildMetadataPanel('deposit', 'deposit', this.buildMetadataEntries(depositAndDepositFolderMetadata)),
            this.buildMetadataPanel('standard', 'standard', this.buildMetadataEntries(standardMetadata ?? {})),
        ];
    });

    private buildMetadataEntries(metadata: Metadata): MetadataEntry[] {
        return Object.entries(metadata).map(([label, value]: [string, string | null]) => ({
            label,
            value,
        }));
    }

    private buildMetadataPanel(key: MetadataPanelKey, label: string, entries: MetadataEntry[]): MetadataPanel {
        return {
            entries: entries.sort(MetadataEntry.sort),
            key,
            label,
        };
    }

    private mergeMetadata(depositFolderMetadata?: Metadata | null, depositMetadata?: Metadata | null): Metadata {
        const mergedMetadata: Metadata = {};

        const metadataKeys = new Set<string>([
            ...Object.keys(depositFolderMetadata ?? {}),
            ...Object.keys(depositMetadata ?? {}),
        ]);

        metadataKeys.forEach((key: string) => {
            const depositFolderItem = depositFolderMetadata?.[key];
            const depositMetadataItem = depositMetadata?.[key];

            mergedMetadata[key] = [depositFolderItem, depositMetadataItem].filter((value) => !!value).join('\n');
        });

        return mergedMetadata;
    }
}
